import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import api from '../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Edit, Trash2, Truck, Send, User, Phone, Car, FileText, Clock, Download, MessageCircle, PackageCheck } from 'lucide-react';
import { formatDate, formatNumber } from '../lib/utils';
import { LRReportForm, LRIssueForm } from '../components/forms';
import { useSettings } from '../contexts/SettingsContext';
import { exportLRToPDF, exportLRIssueToPDF } from '../lib/pdf-export';
import { DriverWhatsAppButton, LRIssueWhatsAppButton } from '../components/ui/whatsapp-button';
import { usePermissions, PermissionGuard } from '../contexts/PermissionsContext';

const LRDetails = () => {
  const params = useParams();
  const lrId = params['*'] || params.lrId;
  const navigate = useNavigate();
  const { hasModulePermission } = usePermissions();
  const { controls } = useSettings();
  const [lr, setLR] = useState(null);
  const [issueHistory, setIssueHistory] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isIssuing, setIsIssuing] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    fetchData();
  }, [lrId]);

  const fetchData = async () => {
    try {
      const [lrRes, historyRes, receiptsRes] = await Promise.allSettled([
        api.get(`/lr-reports/${lrId}`),
        api.get(`/lr-reports/${lrId}/issue-history`),
        api.get(`/grey-receipts?lr_id=${encodeURIComponent(lrId)}`)
      ]);
      if (lrRes.status === 'fulfilled') setLR(lrRes.value.data);
      setIssueHistory(historyRes.status === 'fulfilled' ? historyRes.value.data : []);
      setReceipts(receiptsRes.status === 'fulfilled' ? receiptsRes.value.data : []);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load LR details');
      setLoading(false);
    }
  };

  const handleEditSuccess = () => {
    setIsEditing(false);
    fetchData();
  };

  const handleIssueSuccess = () => {
    setIsIssuing(false);
    fetchData();
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/lr-reports/${lrId}`);
      toast.success('LR Report deleted successfully!');
      navigate('/lr-reports');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete LR');
    }
  };

  // Unified LR Status colors
  const statusColors = {
    'In Transport': 'bg-blue-50 text-blue-700 border-blue-200',
    'Issued for Delivery': 'bg-amber-50 text-amber-700 border-amber-200',
    'Delivered': 'bg-green-50 text-green-700 border-green-200'
  };

  // Check if LR can be issued
  const canIssue = lr && lr.status === 'In Transport';

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!lr) {
    return (
      <div className="p-8 text-center">
        <Truck className="h-16 w-16 mx-auto mb-4 text-slate-300" />
        <p className="text-slate-600">LR Report not found</p>
      </div>
    );
  }

  return (
    <div data-testid="lr-details-page" className="p-4 md:p-6 lg:p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-mono">
              {lr.lr_number}
            </h1>
            <p className="text-slate-600 mt-1">LR Report Details</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {hasModulePermission('lr_reports', 'export_pdf') && controls.enable_pdf_export && (
            <Button
              variant="outline"
              onClick={() => exportLRToPDF(lr, { order_number: lr.order_number })}
              title="Export as PDF"
              data-testid="lr-pdf-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              PDF
            </Button>
            )}
            
            {/* Issue for Delivery Button */}
            {canIssue && hasModulePermission('lr_delivery_issues', 'create') && (
              <Sheet open={isIssuing} onOpenChange={setIsIssuing}>
                <Button
                  data-testid="issue-lr-btn"
                  className="bg-amber-600 hover:bg-amber-700"
                  onClick={() => setIsIssuing(true)}
                >
                  <Send className="h-4 w-4 mr-2" />
                  Issue for Delivery
                </Button>
                <SheetContent className="w-full sm:max-w-md overflow-y-auto">
                  <SheetHeader>
                    <SheetTitle>Issue LR for Delivery</SheetTitle>
                  </SheetHeader>
                  <div className="mt-6">
                    <LRIssueForm 
                      lrIds={[lrId]}
                      lrNumbers={[lr.lr_number]}
                      onSuccess={handleIssueSuccess}
                      onCancel={() => setIsIssuing(false)}
                    />
                  </div>
                </SheetContent>
              </Sheet>
            )}
            
            {/* Cannot issue message */}
            {!canIssue && lr.status !== 'In Transport' && (
              <Badge className={statusColors[lr.status]}>
                {lr.status === 'Issued for Delivery' ? 'Already Issued' : 'Delivered'}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* LR Information Card */}
      <Card className="border border-slate-200 mb-6">
        <CardHeader>
          <CardTitle>LR Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-slate-500 mb-1">Order Number</p>
              {hasModulePermission('grey_orders', 'view') ? (
                <Badge 
                  variant="outline" 
                  className="cursor-pointer hover:bg-slate-100 font-mono"
                  onClick={() => navigate(`/grey-orders/${lr.order_id}`)}
                >
                  {lr.order_number}
                </Badge>
              ) : (
                <span className="font-mono font-medium text-slate-900">{lr.order_number}</span>
              )}
            </div>
            <div>
              <p className="text-sm text-slate-500 mb-1">LR Number (Transport)</p>
              <p className="font-medium text-slate-900">{lr.lr_number_transport}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500 mb-1">Transport</p>
              <p className="font-medium text-slate-900">{lr.transport_name}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500 mb-1">LR Date</p>
              <p className="font-medium text-slate-900">{formatDate(lr.lr_date)}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500 mb-1">Bales in LR</p>
              <p className="font-medium text-slate-900 text-lg">{lr.bales_in_lr}</p>
            </div>
            {(lr.material_quantity != null || lr.material_unit) && (
              <div>
                <p className="text-sm text-slate-500 mb-1">Material Quantity</p>
                <p className="font-medium text-slate-900 text-lg" data-testid="lr-material-qty">
                  {lr.material_quantity != null ? lr.material_quantity : '—'} <span className="text-sm font-normal text-slate-500">{lr.material_unit || ''}</span>
                </p>
              </div>
            )}
            <div>
              <p className="text-sm text-slate-500 mb-1">Status</p>
              <Badge className={statusColors[lr.status] || 'bg-slate-100 text-slate-700'}>
                {lr.status}
              </Badge>
            </div>
            <div>
              <p className="text-sm text-slate-500 mb-1">Created By</p>
              <p className="font-medium text-slate-900">{lr.created_by_name}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Delivery Issue History Card */}
      <Card className="border border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="h-5 w-5 text-amber-600" />
            Delivery Issue History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {issueHistory.length > 0 ? (
            <div className="space-y-4">
              {issueHistory.map((issue) => (
                <div 
                  key={issue.issue_id} 
                  className="p-4 bg-amber-50/50 border border-amber-200 rounded-lg"
                >
                  <div className="flex items-center justify-between mb-3">
                    <Badge className="bg-amber-100 text-amber-800 border-amber-300 font-mono">
                      {issue.issue_number}
                    </Badge>
                    <div className="flex items-center gap-2">
                      {controls.enable_whatsapp && <LRIssueWhatsAppButton issue={issue} />}
                      {controls.enable_pdf_export && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => exportLRIssueToPDF(issue, issue.items || [])}
                        className="gap-1.5"
                        data-testid="lr-issue-pdf-btn"
                      >
                        <Download className="h-4 w-4" />
                        PDF
                      </Button>
                      )}
                      <span className="text-sm text-slate-500">
                        {formatDate(issue.issue_date)}
                      </span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">Driver Name</p>
                        <p className="font-medium text-slate-900">{issue.driver_name}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">Driver Mobile</p>
                        <p className="font-medium text-slate-900">{issue.driver_mobile}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Car className="h-4 w-4 text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">Vehicle Number</p>
                        <p className="font-medium text-slate-900">{issue.vehicle_number}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">Gate Pass Number</p>
                        <p className="font-medium text-slate-900">{issue.gate_pass_no}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">Issued By</p>
                        <p className="font-medium text-slate-900">{issue.issued_by}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">Created By</p>
                        <p className="font-medium text-slate-900">{issue.created_by_name}</p>
                      </div>
                    </div>
                  </div>
                  
                  {issue.remarks && (
                    <div className="mt-3 pt-3 border-t border-amber-200">
                      <p className="text-xs text-slate-500 mb-1">Remarks</p>
                      <p className="text-sm text-slate-700">{issue.remarks}</p>
                    </div>
                  )}
                  
                  {/* Show other LRs in the same issue (for bulk issues) */}
                  {issue.items && issue.items.length > 1 && (
                    <div className="mt-3 pt-3 border-t border-amber-200">
                      <p className="text-xs text-slate-500 mb-2">
                        This was part of a bulk issue with {issue.items.length} LRs:
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {issue.items.map((item) => (
                          <Badge 
                            key={item.item_id}
                            variant={item.lr_id === lrId ? 'default' : 'outline'}
                            className={`text-xs font-mono ${item.lr_id === lrId ? 'bg-amber-600' : 'cursor-pointer hover:bg-slate-100'}`}
                            onClick={() => item.lr_id !== lrId && navigate(`/lr-reports/${item.lr_id}`)}
                          >
                            {item.lr_number}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Send className="h-12 w-12 mx-auto mb-2 text-slate-300" />
              <p>No delivery issue history</p>
              {canIssue && (
                <p className="text-sm mt-1">Click "Issue for Delivery" to issue this LR</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Receipt History */}
      <Card data-testid="lr-receipt-history">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PackageCheck className="h-5 w-5 text-teal-600" />
            Receipt History
            {receipts.length > 0 && (
              <Badge variant="outline" className="ml-1">{receipts.length}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {receipts.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Receipt #</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead className="text-right">Received Bales</TableHead>
                    <TableHead className="text-right">Material Qty</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead>Received By</TableHead>
                    <TableHead>Remarks</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {receipts.map((r) => (
                    <TableRow
                      key={r.receipt_id}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => navigate(`/grey-receipts/${r.receipt_id}`)}
                      data-testid={`receipt-row-${r.receipt_id}`}
                    >
                      <TableCell className="font-mono text-sm font-medium text-teal-700">{r.receipt_number}</TableCell>
                      <TableCell>{formatDate(r.received_date)}</TableCell>
                      <TableCell>{r.location_name || '—'}</TableCell>
                      <TableCell className="text-right font-medium">{formatNumber(r.received_quantity)}</TableCell>
                      <TableCell className="text-right font-medium">{r.received_material_quantity != null ? formatNumber(r.received_material_quantity) : '—'}</TableCell>
                      <TableCell className="text-slate-500">{r.material_unit || '—'}</TableCell>
                      <TableCell>{r.verified_by || r.created_by_name || '—'}</TableCell>
                      <TableCell className="max-w-xs truncate text-slate-600">{r.discrepancy_notes || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <PackageCheck className="h-12 w-12 mx-auto mb-2 text-slate-300" />
              <p>No receipts linked to this LR yet</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LRDetails;
